import React from "react";
import { useNavigate } from "react-router-dom";
import { trackAction } from "../lib/api";

/**
 * Route → path map. Phase 1 sends every mini-app CTA to its stub page.
 * Later phases will open these as overlays/drawers per spec, but the
 * prop contract already matches so no consumer code changes.
 */
const ROUTE_MAP = {
  "sovereign-audit": "/audit",
  "gcpp-model": "/audit",
  "request-data-room": "/partner",
  "partnership-rfp": "/partner",
  "register-deal": "/partners",
  "reserve-block": "/reserve",
  "book-tour": "/visit",
  // Phase 3: submit-rfp is a special-case route — it doesn't navigate, it
  // opens an in-page drawer on /audit. Consumers hand it a `to` prop instead
  // (rendered as an inline anchor with a click handler owned by the parent).
  "submit-rfp": "#submit-rfp",
};

/**
 * Inline prose CTA. Styled as a hyperlink with a warm gold underline glow —
 * never as a button block, per 04_COMPONENT_AND_TECH_SPEC.md §3.2.
 *
 * Fires a fire-and-forget analytics event to `/api/analytics/action` so we
 * can later show a "recent access requests" ticker on the funder gateway.
 */
export default function Action({
  route,
  audience,
  source,
  sourceCell,
  sourceState,
  onClick,
  to: toOverride,
  queryParams,
  children,
  className = "",
}) {
  const navigate = useNavigate();
  const base = ROUTE_MAP[route] || "/";
  const withQuery = queryParams
    ? `${base}?${new URLSearchParams(queryParams).toString()}`
    : base;
  const to = toOverride || withQuery;

  const handleClick = (e) => {
    e.preventDefault();
    trackAction({
      route,
      source: source || audience || null,
      sourceCell: sourceCell || null,
      sourceState: sourceState || null,
      audience: audience || null,
    });
    if (onClick) {
      onClick(e);
      return;
    }
    navigate(to);
  };

  return (
    <a
      href={to}
      onClick={handleClick}
      className={`gg-action ${className}`}
      data-testid={`action-${route}`}
      data-source-cell={sourceCell || undefined}
      data-source-state={sourceState || undefined}
      aria-label={`${children} — opens ${route}`}
    >
      {children}
    </a>
  );
}
